import React, { useEffect, useState, useMemo, useRef } from "react";
import Navbar from "../../Navbar";
import { UrlApi } from "../../../services/apirest";
import {
    Container,
    Typography,
    ListItemIcon,
    Button,
    MenuItem,
    TextField,
    Menu,
    Box,
    Divider,
    useMediaQuery,
} from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import AddBoxTwoToneIcon from "@mui/icons-material/AddBoxTwoTone";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Edit } from "@mui/icons-material";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import MaterialReactTable from "material-react-table";
import { createTheme, ThemeProvider, useTheme } from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import Fichacomprobante from "./Fichacomprobante";
import moment from "moment";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import DeleteDialog from "../../Dialogs/DeleteDialog";
import { logEventos } from "../../../services/services";
import AddComprobantes from "./altacomprobantes/AddComprobantes";
import EditarComprobante from "./editarcomprobante/EditarComprobante";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

// ── Filter functions (stable references outside component) ─────────────────────
const selectFilterFn = (row, id, filterValue) => {
    if (!filterValue) return true;
    const val = row.getValue(id);
    if (val == null || val === "") return false;
    return String(val) === String(filterValue);
};
selectFilterFn.autoRemove = (val) => !val;

const textFilterFn = (row, id, filterValue) => {
    if (!filterValue) return true;
    const val = row.getValue(id);
    if (val == null) return false;
    return String(val).toLowerCase().includes(String(filterValue).toLowerCase());
};
textFilterFn.autoRemove = (val) => !val;

// ── Estado badge ───────────────────────────────────────────────────────────────
const ESTADO_STYLES = {
    Ingresada: { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" },
    Aprobada:  { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
    Anulada:   { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" },
    Pendiente: { bg: "#fef3c7", color: "#92400e", border: "#fcd34d" },
    Pagada:    { bg: "#f0fdf4", color: "#166534", border: "#bbf7d0" },
};

const EstadoBadge = ({ value }) => {
    const s = ESTADO_STYLES[value] || { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" };
    return (
        <Box sx={{
            display: "inline-flex",
            alignItems: "center",
            px: "7px",
            py: "2px",
            borderRadius: "5px",
            fontSize: "0.67rem",
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            backgroundColor: s.bg,
            color: s.color,
            border: `1px solid ${s.border}`,
            whiteSpace: "nowrap",
        }}>
            {value || "—"}
        </Box>
    );
};

// ── Component ──────────────────────────────────────────────────────────────────
export default function Comprobantes() {
    const [comprobantes, setComprobantes] = useState([]);
    const [tabla, setTabla]   = useState(true);
    const [alta, setAlta]     = useState(false);
    const [ficha, setFicha]   = useState(false);
    const [anular, setAnular] = useState(false);
    const [editar, setEditar] = useState(false);
    const [fechaDesde, setFechaDesde] = useState("");
    const [fechaHasta, setFechaHasta] = useState("");
    const [datosComprobantes, setDatosComprobantes] = useState({});
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
    const tableInstanceRef = useRef(null);
    const globalTheme = useTheme();
    const isMobile = useMediaQuery(globalTheme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(globalTheme.breakpoints.down("md"));
    const [notify, setNotify] = useState({ open: false, mensaje: "", severity: "", color: "" });

    // ── Data ───────────────────────────────────────────────────────────────────
    const obtenerComprobantes = () => {
        fetch(UrlApi + "/comprobantes_cabecera/joincuit")
            .then((d) => d.json())
            .then((d) => setComprobantes(d));
    };

    useEffect(() => { obtenerComprobantes(); }, [tabla]);
    useEffect(() => {
        logEventos("Acceso al menú Ingreso Facturas", "Compras", "Comprobantes", localStorage.getItem("user"));
    }, []);

    // ── Handlers ───────────────────────────────────────────────────────────────
    const handleClickNuevo = () => { setAlta(true); setTabla(false); };
    const handleEditar     = (data) => { setEditar(true); setDatosComprobantes(data); setTabla(false); };

    const handleAnular = async (e) => {
        try {
            e.preventDefault();
            await fetch(UrlApi + "/comprobantes_cabecera/" + datosComprobantes.idcomprobante, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
            });
        } catch (err) {
            console.error("Error al anular:", err);
            alert("Hubo un error al guardar los datos");
        }
        setAnular(false);
        setTabla(true);
        obtenerComprobantes();
        logEventos("Anular comprobantes", "Ingreso facturas", "Compras", localStorage.getItem("user"));
    };

    // ── Filtered rows ──────────────────────────────────────────────────────────
    const comprobantesFiltrados = useMemo(() => {
        if (!comprobantes.length) return [];
        return comprobantes.filter((c) => {
            const fecha = c.fecha_factura ? moment(c.fecha_factura) : null;
            if (fechaDesde && fecha && fecha.isBefore(moment(fechaDesde), "day")) return false;
            if (fechaHasta && fecha && fecha.isAfter(moment(fechaHasta), "day")) return false;
            return true;
        });
    }, [comprobantes, fechaDesde, fechaHasta]);

    // ── Select options ─────────────────────────────────────────────────────────
    const uniqueValues = (key) =>
        [...new Set(comprobantes.map((c) => c[key]).filter(Boolean))].sort();

    const tiposOpciones      = useMemo(() => uniqueValues("tipo_comprobante"), [comprobantes]);
    const empresasOpciones   = useMemo(() => uniqueValues("empresa"),          [comprobantes]);
    const sucursalesOpciones = useMemo(() => uniqueValues("nombre"),           [comprobantes]);
    const estadosOpciones    = useMemo(() => uniqueValues("estado"),           [comprobantes]);

    // ── Columns ────────────────────────────────────────────────────────────────
    const columns = useMemo(() => [
        {
            header: "Tipo",
            accessorKey: "tipo_comprobante",
            accessorFn: (c) => c.tipo_comprobante ?? "",
            size: 120,
            filterVariant: "select",
            filterSelectOptions: tiposOpciones,
            filterFn: selectFilterFn,
        },
        {
            header: "Núm.",
            accessorKey: "nro_comprobante",
            accessorFn: (c) => c.nro_comprobante ?? "Sin registrar",
            size: 100,
            filterFn: textFilterFn,
            muiTableBodyCellProps: { align: "left" },
        },
        {
            header: "Proveedor",
            accessorKey: "razon_social",
            accessorFn: (c) => c.razon_social ?? "",
            size: 200,
            filterFn: textFilterFn,
            muiTableBodyCellProps: { align: "left" },
        },
        {
            header: "Empresa",
            accessorKey: "empresa",
            accessorFn: (c) => c.empresa ?? "",
            size: 180,
            filterVariant: "select",
            filterSelectOptions: empresasOpciones,
            filterFn: selectFilterFn,
            muiTableBodyCellProps: { align: "left" },
        },
        {
            header: "Sucursal",
            accessorKey: "nombre",
            accessorFn: (c) => c.nombre ?? "",
            size: 150,
            filterVariant: "select",
            filterSelectOptions: sucursalesOpciones,
            filterFn: selectFilterFn,
            muiTableBodyCellProps: { align: "left" },
        },
        {
            header: "Fecha",
            accessorKey: "fecha_factura",
            accessorFn: (c) => c.fecha_factura ? moment(c.fecha_factura).format("DD-MM-YYYY") : "",
            size: 100,
            filterFn: textFilterFn,
            muiTableBodyCellProps: { align: "center" },
        },
        {
            header: "Total",
            id: "total",
            accessorFn: (c) =>
                c.total === null
                    ? "No corresponde"
                    : new Intl.NumberFormat("es-AR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }).format(c.total),
            size: 130,
            filterFn: textFilterFn,
            muiTableBodyCellProps: { align: "right" },
        },
        {
            header: "Estado",
            accessorKey: "estado",
            accessorFn: (c) => c.estado ?? "",
            size: 120,
            filterVariant: "select",
            filterSelectOptions: estadosOpciones,
            filterFn: selectFilterFn,
            Cell: ({ cell }) => <EstadoBadge value={cell.getValue()} />,
            muiTableBodyCellProps: { align: "center" },
        },
        {
            header: "Vencimiento",
            accessorKey: "vto_factura",
            accessorFn: (c) =>
                c.vto_factura === null ? "No corresponde" : moment(c.vto_factura).format("DD-MM-YYYY"),
            size: 110,
            filterFn: textFilterFn,
            muiTableBodyCellProps: { align: "center" },
        },
    ], [tiposOpciones, empresasOpciones, sucursalesOpciones, estadosOpciones]);

    // ── Export ─────────────────────────────────────────────────────────────────
    const EXPORT_HEADERS = ["Tipo", "Número", "Proveedor", "Empresa", "Sucursal", "Fecha", "Total", "Estado", "Vencimiento"];

    const getExportRows = () => {
        const rows = tableInstanceRef.current?.getFilteredRowModel().rows ?? [];
        return rows.map(({ original: c }) => [
            c.tipo_comprobante ?? "",
            c.nro_comprobante  ?? "Sin registrar",
            c.razon_social     ?? "",
            c.empresa          ?? "",
            c.nombre           ?? "",
            c.fecha_factura ? moment(c.fecha_factura).format("DD-MM-YYYY") : "",
            c.total === null
                ? "No corresponde"
                : new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(c.total),
            c.estado ?? "",
            c.vto_factura === null ? "No corresponde" : moment(c.vto_factura).format("DD-MM-YYYY"),
        ]);
    };

    const exportToXLSX = () => {
        const data = getExportRows();
        const ws   = XLSX.utils.aoa_to_sheet([EXPORT_HEADERS, ...data]);
        ws["!cols"] = [10, 12, 28, 20, 18, 12, 18, 12, 14].map((wch) => ({ wch }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Comprobantes");
        XLSX.writeFile(wb, `Comprobantes_${moment().format("DD-MM-YYYY")}.xlsx`);
        setExportMenuAnchor(null);
    };

    const exportToPDF = () => {
        const data = getExportRows();
        const doc  = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(14);
        doc.text("Comprobantes", 14, 12);
        doc.setFontSize(9);
        doc.text(`Exportado: ${moment().format("DD/MM/YYYY HH:mm")}`, 14, 18);
        doc.autoTable({
            head: [EXPORT_HEADERS],
            body: data,
            startY: 22,
            styles:            { fontSize: 7, cellPadding: 2 },
            headStyles:        { fillColor: [15, 34, 64], textColor: 255, fontStyle: "bold" },
            alternateRowStyles:{ fillColor: [239, 246, 255] },
        });
        doc.save(`Comprobantes_${moment().format("DD-MM-YYYY")}.pdf`);
        setExportMenuAnchor(null);
    };

    // ── MRT theme ──────────────────────────────────────────────────────────────
    const tableHeadCellProps = {
        align: "center",
        sx: {
            fontSize: 12,
            color: "white",
            fontWeight: 600,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            background: "linear-gradient(180deg, #1e3a5f 0%, #17304f 100%)",
            borderRight: "1px solid rgba(255,255,255,0.07)",
            py: 1.5,
            "&:last-child": { borderRight: "none" },
        },
    };

    const tableTheme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode: globalTheme.palette.mode,
                    primary: globalTheme.palette.primary,
                    info: { main: "rgb(255,122,0)" },
                    background: {
                        default: globalTheme.palette.mode === "light" ? "#f0f4f8" : "#0a0f1a",
                    },
                },
                typography: {
                    button: { textTransform: "none", fontSize: "0.85rem", fontWeight: 500 },
                },
                components: {
                    MuiTooltip: { styleOverrides: { tooltip: { fontSize: "0.75rem" } } },
                },
            }),
        [globalTheme]
    );

    // ── Toolbar ────────────────────────────────────────────────────────────────
    const renderToolbar = () => (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: { xs: 1, sm: 1.5 },
                width: "100%",
                py: 0.25,
            }}
        >
            {/* Title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: { xs: 0, sm: 0.5 } }}>
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
                    boxShadow: "0 2px 8px rgba(37,99,235,0.28)",
                    flexShrink: 0,
                }}>
                    <AssignmentIcon sx={{ color: "white", fontSize: 17 }} />
                </Box>
                {!isMobile && (
                    <Typography sx={{
                        fontSize: isTablet ? 17 : 20,
                        fontWeight: 700,
                        color: "#0f2240",
                        letterSpacing: "-0.02em",
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                    }}>
                        Comprobantes
                    </Typography>
                )}
            </Box>

            {/* Nuevo */}
            <Button
                variant="contained"
                size="small"
                startIcon={<AddBoxTwoToneIcon />}
                onClick={handleClickNuevo}
                sx={{
                    background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
                    boxShadow: "0 2px 8px rgba(37,99,235,0.28)",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    px: 1.5,
                    py: 0.7,
                    whiteSpace: "nowrap",
                    "&:hover": {
                        background: "linear-gradient(135deg, #162d4a 0%, #1d4ed8 100%)",
                        boxShadow: "0 4px 14px rgba(37,99,235,0.38)",
                        transform: "translateY(-1px)",
                    },
                    transition: "all 0.18s ease",
                }}
            >
                {isMobile ? "Nuevo" : "Nuevo Comprobante"}
            </Button>

            {/* Date range */}
            <Box sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.75,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "10px",
                px: 1.25,
                py: 0.5,
            }}>
                <TextField
                    label="Desde"
                    type="date"
                    size="small"
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                        minWidth: 138,
                        "& .MuiInputBase-root": {
                            fontSize: "0.78rem",
                            borderRadius: "6px",
                            backgroundColor: "#fff",
                        },
                        "& .MuiInputLabel-root": { fontSize: "0.78rem" },
                    }}
                />
                <Box component="span" sx={{ color: "#94a3b8", fontSize: "0.8rem", userSelect: "none" }}>→</Box>
                <TextField
                    label="Hasta"
                    type="date"
                    size="small"
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                        minWidth: 138,
                        "& .MuiInputBase-root": {
                            fontSize: "0.78rem",
                            borderRadius: "6px",
                            backgroundColor: "#fff",
                        },
                        "& .MuiInputLabel-root": { fontSize: "0.78rem" },
                    }}
                />
                {(fechaDesde || fechaHasta) && (
                    <Button
                        size="small"
                        variant="text"
                        color="error"
                        onClick={() => { setFechaDesde(""); setFechaHasta(""); }}
                        sx={{ fontSize: "0.75rem", px: 0.75, py: 0.4, borderRadius: "6px", minWidth: "auto" }}
                    >
                        Limpiar
                    </Button>
                )}
            </Box>

            {/* Export */}
            <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                sx={{
                    borderColor: "#2563eb",
                    color: "#2563eb",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    px: 1.5,
                    py: 0.7,
                    whiteSpace: "nowrap",
                    "&:hover": {
                        borderColor: "#1d4ed8",
                        backgroundColor: "#eff6ff",
                        transform: "translateY(-1px)",
                    },
                    transition: "all 0.18s ease",
                }}
            >
                Exportar
            </Button>
            <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
                PaperProps={{
                    sx: {
                        borderRadius: "10px",
                        boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
                        border: "1px solid #e2e8f0",
                        minWidth: 165,
                        mt: 0.5,
                    },
                }}
            >
                <MenuItem onClick={exportToXLSX} sx={{ fontSize: "0.85rem", py: 1, gap: 1 }}>
                    📊&nbsp; Exportar XLSX
                </MenuItem>
                <MenuItem onClick={exportToPDF}  sx={{ fontSize: "0.85rem", py: 1, gap: 1 }}>
                    📄&nbsp; Exportar PDF
                </MenuItem>
            </Menu>
        </Box>
    );

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: "#f0f4f8" }}>
            <Navbar />

            {tabla && comprobantes.length > 0 && (
                <Container maxWidth="xl" sx={{ pt: 2.5, pb: 5 }}>
                    {/* Card wrapper */}
                    <Box sx={{
                        borderRadius: "14px",
                        overflow: "hidden",
                        boxShadow: "0 2px 16px rgba(15,34,64,0.09), 0 1px 4px rgba(15,34,64,0.05)",
                        border: "1px solid #e2e8f0",
                    }}>
                        {/* Accent top bar */}
                        <Box sx={{
                            height: "4px",
                            background: "linear-gradient(90deg, #1e3a5f 0%, #2563eb 55%, #60a5fa 100%)",
                        }} />

                        <ThemeProvider theme={tableTheme}>
                            <MaterialReactTable
                                tableInstanceRef={tableInstanceRef}
                                enableColumnResizing
                                columns={columns}
                                data={comprobantesFiltrados}
                                initialState={{
                                    density: "compact",
                                    showGlobalFilter: true,
                                    showColumnFilters: true,
                                    pagination: { pageSize: 15, pageIndex: 0 },
                                }}
                                positionActionsColumn="last"
                                enableExpandAll={false}
                                enableToolbarInternalActions={true}
                                muiTableHeadCellProps={tableHeadCellProps}
                                localization={MRT_Localization_ES}
                                muiTableBodyCellProps={{
                                    align: "left",
                                    sx: {
                                        color: "#1e293b",
                                        borderColor: "#f1f5f9",
                                        fontSize: "0.82rem",
                                        py: 0.65,
                                    },
                                }}
                                muiTableBodyRowProps={({ row }) => ({
                                    sx: {
                                        backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#f9fbfd",
                                        transition: "background-color 0.15s ease",
                                        "&:hover td": {
                                            backgroundColor: "#eff6ff !important",
                                        },
                                    },
                                })}
                                muiTablePaperProps={{
                                    sx: {
                                        borderRadius: 0,
                                        boxShadow: "none",
                                        border: "none",
                                        backgroundColor: "transparent",
                                    },
                                }}
                                muiTopToolbarProps={{
                                    sx: {
                                        backgroundColor: "#ffffff",
                                        borderBottom: "1px solid #f1f5f9",
                                        px: { xs: 1.5, sm: 2 },
                                        py: 1.25,
                                        flexWrap: "wrap",
                                        gap: 1,
                                    },
                                }}
                                muiBottomToolbarProps={{
                                    sx: {
                                        backgroundColor: "#f8fafc",
                                        borderTop: "1px solid #e8eef4",
                                        minHeight: 52,
                                    },
                                }}
                                muiTableContainerProps={{
                                    sx: { maxHeight: "calc(100vh - 270px)" },
                                }}
                                enableColumnActions={false}
                                muiSearchTextFieldProps={{
                                    placeholder: "Buscar…",
                                    sx: {
                                        minWidth: isMobile ? 140 : 200,
                                        "& .MuiInputBase-root": {
                                            borderRadius: "8px",
                                            backgroundColor: "#f8fafc",
                                            fontSize: "0.83rem",
                                        },
                                    },
                                    variant: "outlined",
                                    size: "small",
                                }}
                                enableDensityToggle={false}
                                enableColumnFilters={true}
                                enableHiding={false}
                                enableColumnDragging={false}
                                layoutMode="grid"
                                enableRowActions
                                positionExpandColumn="first"
                                renderTopToolbarCustomActions={renderToolbar}
                                renderRowActionMenuItems={({ closeMenu, row }) => [
                                    <MenuItem
                                        key="edit"
                                        style={{ display: row.original.activo === 0 ? "none" : undefined }}
                                        disabled={row.original.activo === 0}
                                        onClick={() => { handleEditar(row.original); closeMenu(); }}
                                        sx={{ m: 0, gap: 1, fontSize: "0.85rem" }}
                                    >
                                        <ListItemIcon sx={{ minWidth: "auto" }}>
                                            <Edit color="primary" fontSize="small" />
                                        </ListItemIcon>
                                        Editar
                                    </MenuItem>,
                                    <MenuItem
                                        key="anular"
                                        onClick={() => { closeMenu(); setDatosComprobantes(row.original); setAnular(true); }}
                                        sx={{ m: 0, gap: 1, fontSize: "0.85rem" }}
                                    >
                                        <ListItemIcon sx={{ minWidth: "auto" }}>
                                            <HighlightOffIcon color="error" fontSize="small" />
                                        </ListItemIcon>
                                        Anular
                                    </MenuItem>,
                                    <Divider key="div" />,
                                    <MenuItem
                                        key="ficha"
                                        style={{ display: row.original.activo === 0 ? "none" : undefined }}
                                        disabled={row.original.activo === 0}
                                        onClick={() => { closeMenu(); setFicha(true); setTabla(false); setDatosComprobantes(row.original); }}
                                        sx={{ m: 0, gap: 1, fontSize: "0.85rem" }}
                                    >
                                        <ListItemIcon sx={{ minWidth: "auto" }}>
                                            <RemoveRedEyeIcon color="success" fontSize="small" />
                                        </ListItemIcon>
                                        Ver ficha comprobante
                                    </MenuItem>,
                                ]}
                            />
                        </ThemeProvider>
                    </Box>
                </Container>
            )}

            {alta  && <AddComprobantes alta={setAlta} tabla={setTabla} datos={datosComprobantes} />}
            {ficha && <Fichacomprobante ficha={setFicha} tabla={setTabla} datos={datosComprobantes} />}

            {anular && (
                <DeleteDialog
                    title="Anular comprobante"
                    confirmText="¿Confirma que desea anular el comprobante?"
                    btnText="Confirmar"
                    notify={notify}
                    setNotify={setNotify}
                    open={anular}
                    setOpen={setAnular}
                    guardar={handleAnular}
                />
            )}

            {editar && (
                <EditarComprobante
                    open={editar}
                    setOpen={setEditar}
                    datos={datosComprobantes}
                    tabla={setTabla}
                />
            )}
        </Box>
    );
}

//========================================================================
/*----- Component --------------------------------------------------------/
// Developer     : Candela Grosso
// Creation Date : 29/5/2024
// Version       : 1.1 (diseño responsivo refinado)
// Description   : Comprobantes con MRT v1. Filtros por columna, export
//                 XLSX/PDF, badges de estado con color, toolbar responsive.
//========================================================================*/
